<template>
  <b-overlay :show="formShow" rounded="sm" no-fade class="mt-1">
    <b-card>
      <b-row class="mb-1">
        <b-col
          cols="12"
          md="7"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <b-button
            variant="outline-primary"
            class="btn-icon"
            @click="$router.go(-1)"
          >
            <i class="fa-solid fa-arrow-left"></i>
            <span class="ml-25">Back</span>
          </b-button>
        </b-col>
      </b-row>

      <b-row class="mb-1">
        <table class="table table-sm">
          <thead>
            <tr>
              <th scope="col">
                {{ actionId ? "Action Edit" : "Action Add" }}
              </th>
            </tr>
          </thead>
        </table>
      </b-row>

      <validation-observer ref="actionRules">
        <b-form>
          <b-row>
            <b-col cols="6" md="12" sm="12">
              <b-form-group label="Action Title" label-for="title">
                <validation-provider
                  name="title"
                  #default="{ errors }"
                  rules="required"
                >
                  <textarea
                    v-model="actionValue.title"
                    class="form-control"
                    id="title"
                    name="title"
                    rows="3"
                  ></textarea>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="6" md="12" sm="12">
              <b-form-group label="Objective" label-for="objective">
                <validation-provider
                  name="objective"
                  #default="{ errors }"
                  rules="required"
                >
                  <v-select
                    v-model="actionValue.objective_id"
                    label="title"
                    :reduce="(val) => val.id"
                    :options="objectives"
                    :clearable="true"
                  ></v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <hr />

          <b-row>
            <b-col cols="12">
              <b-button
                type="submit"
                @click.prevent="formSubmitted"
                variant="primary"
                class="float-right"
              >
                {{ actionId ? "Update Action" : "Create Action" }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>
  </b-overlay>
</template>

<script>
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../settingsStoreModule";
import vSelect from "vue-select";
import router from "@/router";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";

export default {
  components: {
    vSelect,
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },

  setup() {
    const ACTION_CODE_APP_STORE_MODULE_NAME = "settings";

    if (!store.hasModule(ACTION_CODE_APP_STORE_MODULE_NAME)) {
      store.registerModule(
        ACTION_CODE_APP_STORE_MODULE_NAME,
        settingsStoreModule
      );
    }

    onUnmounted(() => {
      if (store.hasModule(ACTION_CODE_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(ACTION_CODE_APP_STORE_MODULE_NAME);
      }
    });
  },

  data() {
    return {
      actionValue: {
        title: "",
        objective_id: "",
      },
      objectives: [],
      formShow: false,
      actionId: null,
    };
  },

  mounted() {
    this.initializeForm();
  },

  methods: {
    async initializeForm() {
      try {
        await this.getObjectives();

        if (router.currentRoute.params.id) {
          this.formShow = true;
          this.actionId = parseInt(router.currentRoute.params.id);
          await this.getActionById();
        }
      } catch (error) {
        this.formShow = false;
        this.handleError("Failed to initialize form.");
      }
    },

    getObjectives() {
      return store.dispatch("settings/getAllObjectives").then((res) => {
        this.objectives = res.data;
      });
    },

    getActionById() {
      return store
        .dispatch("settings/getActionById", { id: this.actionId })
        .then((res) => {
          this.actionValue = res.data;
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          this.formShow = false;
        });
    },

    formSubmitted() {
      this.formShow = true;

      this.$refs.actionRules.validate().then((success) => {
        if (success) {
          const action = this.actionId
            ? "settings/updateAction"
            : "settings/saveAction";

          store
            .dispatch(action, this.actionValue)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Successful",
                  text: this.actionId
                    ? "✔️ Action Update Successful"
                    : "✔️ Action Added Successfully",
                  icon: "ThumbsUpIcon",
                  variant: "success",
                },
              });

              router.push({ name: "settings-actions" });
            })
            .catch(() => {
              this.handleError("An error occurred while saving the action.");
            })
            .finally(() => {
              this.formShow = false;
            });
        } else {
          this.formShow = false;
        }
      });
    },

    handleError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          text: message,
          icon: "AlertCircleIcon",
          variant: "danger",
        },
      });
      this.formShow = false;
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
